<template>
  <div class="report-trait-picker">
    <!-- Activator -->
    <v-btn
      block
      variant="outlined"
      color="primary"
      class="mb-3 justify-space-between"
      @click="open = true"
    >
      <div class="d-flex align-center">
        <v-icon start>mdi-tag-multiple</v-icon>
        {{ title }}
      </div>
      <v-chip
        v-if="selectedIds.length"
        size="x-small"
        color="primary"
        variant="flat"
        class="ml-3"
      >
        {{ selectedIds.length }} / {{ totalTraits }}
      </v-chip>
    </v-btn>

    <!-- Inline selected overview (scrollable chips) -->
    <div
      v-if="selectedIds.length"
      class="d-flex align-center flex-wrap ga-1 mb-2"
    >
      <v-chip
        v-for="chip in selectedChips"
        :key="chip.id"
        size="small"
        variant="tonal"
        closable
        @click:close="toggleTrait(chip.id)"
      >
        <template #prepend>
          <v-icon
            :color="traitsStore.getTraitColor(chip.id)"
            size="small"
            class="trait-icon"
          >
            {{ traitsStore.getTraitIcon(chip.id) }}
          </v-icon>
        </template>
        {{ chip.name }}
      </v-chip>
    </div>

    <!-- Fullscreen dialog for selection -->
    <v-dialog v-model="open" fullscreen transition="dialog-bottom-transition">
      <v-card>
        <v-toolbar density="comfortable" color="primary" dark>
          <v-btn icon @click="open = false"><v-icon>mdi-close</v-icon></v-btn>
          <v-toolbar-title>{{ title }}</v-toolbar-title>
          <v-spacer />
          <v-chip
            size="x-small"
            color="white"
            text-color="primary"
            class="ml-3 mr-2"
          >
            {{ selectedIds.length }} / {{ totalTraits }}
          </v-chip>
          <v-btn size="small" variant="text" @click="clearAll">Wissen</v-btn>
          <v-btn size="small" color="white" text @click="open = false"
            >Gereed</v-btn
          >
        </v-toolbar>

        <v-card-text class="pt-4">
          <!-- Search -->
          <v-text-field
            v-model="search"
            label="Zoeken"
            variant="outlined"
            density="comfortable"
            prepend-inner-icon="mdi-magnify"
            hide-details
            class="mb-3"
            clearable
          />

          <!-- Selected chips inside dialog -->
          <div
            v-if="selectedIds.length"
            class="d-flex align-center flex-wrap ga-2 mb-4"
          >
            <v-chip
              v-for="chip in selectedChips"
              :key="chip.id"
              size="small"
              variant="tonal"
              closable
              @click:close="toggleTrait(chip.id)"
            >
              <template #prepend>
                <v-icon
                  :color="traitsStore.getTraitColor(chip.id)"
                  size="small"
                  class="trait-icon"
                >
                  {{ traitsStore.getTraitIcon(chip.id) }}
                </v-icon>
              </template>
              {{ chip.name }}
            </v-chip>
          </div>

          <!-- Category groups -->
          <div>
            <div v-for="(group, gi) in filteredGroups" :key="gi" class="mb-4">
              <div class="d-flex align-center ga-2 mb-2">
                <v-icon color="primary">mdi-folder</v-icon>
                <span class="text-body-1">{{ group.name }}</span>
                <v-chip
                  v-if="group.selectedCount"
                  size="x-small"
                  color="primary"
                  variant="flat"
                >
                  {{ group.selectedCount }}
                </v-chip>
              </div>
              <v-list density="comfortable">
                <v-list-item
                  v-for="trait in group.children"
                  :key="trait.id"
                  :title="trait.name"
                  lines="two"
                  :class="{ 'bg-selected': isSelected(trait.id) }"
                  @click="toggleTrait(trait.id)"
                >
                  <template #prepend>
                    <v-checkbox-btn
                      :model-value="isSelected(trait.id)"
                      @update:model-value="toggleTrait(trait.id)"
                    />
                  </template>
                </v-list-item>
              </v-list>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useTraitsStoreRefactor } from "@/stores/traitsStoreRefactor";

const props = defineProps({
  modelValue: { type: Array, default: () => [] },
  account_id: { type: [Number, String], required: false },
  title: { type: String, default: "Kenmerken" },
  isNewReport: { type: Boolean, default: false }, // Only show active traits for new reports
});
const emit = defineEmits(["update:modelValue", "change"]);

const open = ref(false);
const search = ref("");
const traitsList = ref([]);
const traitsStore = useTraitsStoreRefactor();

const selectedIds = computed({
  get: () => (Array.isArray(props.modelValue) ? props.modelValue : []),
  set: (val) => emit("update:modelValue", val),
});

const groups = computed(() => {
  const data = traitsList.value || [];
  const categoryLabels = [
    ...new Set(data.map((t) => t?.category).filter(Boolean)),
  ];
  return categoryLabels.map((label, idx) => ({
    id: idx * 100,
    name: label,
    children: data
      .filter((t) => t.category === label)
      .map((t) => ({ id: t.id, name: t.name, category: t.category })),
  }));
});

const totalTraits = computed(() => (traitsList.value || []).length);

const filteredGroups = computed(() => {
  const q = (search.value || "").toLowerCase();
  const filterChild = (child) => child.name.toLowerCase().includes(q);
  return groups.value
    .map((g) => {
      const children = q ? g.children.filter(filterChild) : g.children;
      return {
        ...g,
        children,
        selectedCount: children.filter((c) => selectedIds.value.includes(c.id))
          .length,
      };
    })
    .filter((g) => g.children.length > 0);
});

const selectedChips = computed(() => {
  const idSet = new Set(selectedIds.value);
  const flat = groups.value.flatMap((g) => g.children);
  return flat.filter((c) => idSet.has(c.id));
});

function isSelected(id) {
  return selectedIds.value.includes(id);
}

function toggleTrait(id) {
  const next = [...selectedIds.value];
  const idx = next.indexOf(id);
  if (idx === -1) next.push(id);
  else next.splice(idx, 1);
  selectedIds.value = next;
  emit("change");
}

function clearAll() {
  selectedIds.value = [];
  emit("change");
}

onMounted(async () => {
  const data = await traitsStore.fetchAll();
  let filteredData = Array.isArray(data) ? data.filter(Boolean) : [];

  // Filter out inactive traits for new reports only
  // Use strict check: only show traits where active === true
  if (props.isNewReport) {
    filteredData = filteredData.filter((t) => t.active === true);
  }

  traitsList.value = filteredData.map((t) => ({
    id: t.id,
    name: t.name,
    category: t.category,
  }));
});
</script>

<style scoped>
.bg-selected {
  background-color: rgba(25, 118, 210, 0.06);
}

.trait-icon {
  margin-right: 4px !important;
}
</style>
